import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Form, Spinner, Table } from "react-bootstrap";
import { useSearchParams } from "react-router";
import { apiErrorMessage, useGetLazy, usePut } from "../../api";
import { FilterBar, FilterField, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { ChangeLogTable } from "../../components/workbench/change-log-table";
import { usePermissions } from "../../hooks";
import {
  IMerchantMapEditorView,
  IMerchantMapEditRequest,
  IMerchantMapFieldView,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";

const SECTIONS: Array<{ key: IMerchantMapFieldView["section"]; title: string }> = [
  { key: "product", title: "Product page" },
  { key: "cart", title: "Cart" },
  { key: "coupon", title: "Coupon field" },
  { key: "checkout", title: "Checkout" },
  { key: "shipping", title: "Shipping details" },
];

interface IDraft {
  value: string;
  pin: boolean;
  /** Send the stored value so it is written to staging even though it is unchanged here. */
  stage?: boolean;
}

const PROOF: Record<IMerchantMapFieldView["proof"]["status"], { label: string; bg: string; hint: string }> = {
  proven: { label: "proven", bg: "success", hint: "The latest journey test passed this exact value, so the hourly merge publishes it" },
  failed: { label: "failed", bg: "danger", hint: "The latest journey test failed this field, so it isn't published" },
  "not-resolved": { label: "not resolved", bg: "warning", hint: "The latest journey test passed but never found the element, so it isn't published" },
  "other-value": { label: "other value", bg: "warning", hint: "The latest journey test proved a different value; the stored one isn't published until a test passes it" },
  untested: { label: "untested", bg: "secondary", hint: "No journey test has checked this field yet, so it isn't published" },
};

/** Map values reach the API only once a journey test proves them; SQL fields use them only as a fallback. */
const proofCell = (f: IMerchantMapFieldView) => {
  if (!f.value && f.proof.status === "untested") return <span className="text-muted">—</span>;
  const p = PROOF[f.proof.status];
  return (
    <>
      <Badge bg={p.bg} text={p.bg === "warning" ? "dark" : undefined} title={p.hint}>
        {p.label}
      </Badge>
      {f.proof.testedAt && <span className="text-muted ms-1">{new Date(f.proof.testedAt).toLocaleDateString()}</span>}
      {f.proof.provenValue && <code className="d-block small text-break">{f.proof.provenValue}</code>}
      {f.proof.reason && f.proof.status !== "proven" && <div className="text-muted small">{f.proof.reason}</div>}
      {f.servedFrom === "sql" && <div className="text-muted small">used only if the SQL template is empty</div>}
    </>
  );
};

const served = (f: IMerchantMapFieldView) => {
  if (!f.servedValue && !f.value) return <span className="text-muted">—</span>;
  if (!f.servedValue) return <Badge bg="secondary" title="We store a value but the Open Data API doesn't serve one">not served</Badge>;
  const same = f.servedValue === f.value;
  return (
    <>
      <Badge bg={same ? "success" : "warning"} text={same ? undefined : "dark"} className="me-1" title={same ? "The API serves the stored value" : "The API serves a different value"}>
        {same ? "same" : "differs"}
      </Badge>
      {!same && <code className="small text-break">{f.servedValue}</code>}
    </>
  );
};

const staged = (f: IMerchantMapFieldView, hasStaging: boolean) => {
  if (f.servedFrom !== "sql") return null;
  if (!f.stagingField)
    return (
      <Badge bg="secondary" title="The staging template has no field for this, so the put-live flow can't change what the API serves">
        no staging field
      </Badge>
    );
  if (!hasStaging) return <span className="text-muted" title="No staging template for this domain and view">no template</span>;
  if (!f.stagedValue) return <span className="text-muted">—</span>;
  const live = f.stagedValue === f.servedValue;
  return (
    <>
      <Badge bg={live ? "success" : "warning"} text={live ? undefined : "dark"} className="me-1" title={live ? "The API serves the staged value" : "Staged but the API serves something else; put it live to change that"}>
        {live ? "live" : "not live"}
      </Badge>
      {f.stagedValue !== f.value && <code className="small text-break">{f.stagedValue}</code>}
    </>
  );
};

/** Edit a stored merchant map field by field, see what the API serves, and keep a history of every change. */
export const MerchantMapEditor: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const domain = params.get("domain") ?? "";
  const view = (params.get("view") as "mobile" | "desktop") ?? "mobile";
  const [domainInput, setDomainInput] = useState(domain);
  const [viewInput, setViewInput] = useState(view);
  const [drafts, setDrafts] = useState<Record<string, IDraft>>({});
  const [comment, setComment] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const { execute: load, data, loading, error } = useGetLazy<IMerchantMapEditorView>();
  const { execute: save, loading: saving, error: saveError } = usePut<IMerchantMapEditorView, IMerchantMapEditRequest>();
  const [saved, setSaved] = useState<IMerchantMapEditorView | null>(null);
  const { can, isAdmin } = usePermissions();
  const canEdit = isAdmin || can(PermissionAction.UpdateAny, PermissionResource.Template);
  const map = saved ?? data;

  useEffect(() => {
    if (!domain) return;
    setSaved(null);
    setDrafts({});
    void load(`merchant-map-editor/${encodeURIComponent(domain)}/${view}`);
  }, [domain, view, load]);

  const changes = useMemo(
    () =>
      (map?.fields ?? []).flatMap((f) => {
        const d = drafts[f.path];
        if (!d) return [];
        const valueChanged = (d.value.trim() || null) !== f.value || !!d.stage;
        const pinChanged = d.pin !== !!f.pin;
        if (!valueChanged && !pinChanged) return [];
        return [{ path: f.path, ...(valueChanged ? { value: d.value.trim() || null } : {}), pin: d.pin }];
      }),
    [drafts, map]
  );

  const draftOf = (f: IMerchantMapFieldView): IDraft => drafts[f.path] ?? { value: f.value ?? "", pin: !!f.pin };
  const setDraft = (f: IMerchantMapFieldView, patch: Partial<IDraft>) => setDrafts((all) => ({ ...all, [f.path]: { ...draftOf(f), ...patch } }));

  const submit = async () => {
    const res = await save(`merchant-map-editor/${encodeURIComponent(domain)}/${view}`, { edits: changes, comment }).catch(() => null);
    if (!res) return;
    setSaved(res);
    setDrafts({});
    setComment("");
    setRefreshKey((k) => k + 1);
  };

  return (
    <div>
      <PageHeader
        title="Merchant map editor"
        description="Set or fix a store's map fields by hand. Pinned fields keep their value through automated captures and self-heal. Every change is recorded with who, when and why."
      />
      <PageToolbar>
        <ToolbarButton label="Templates" icon="bi-arrow-left" to="/auto-coupons/templates" hint="Back to the coupon templates, where staged changes are put live" />
      </PageToolbar>

      <FilterBar
        onSearch={() => setParams({ domain: domainInput.trim().toLowerCase(), view: viewInput })}
        searchLabel="Load map"
        defaultOpen
      >
        <FilterField label="Domain" hint="The store, e.g. florsheim.com">
          <Form.Control size="sm" value={domainInput} placeholder="florsheim.com" onChange={(e) => setDomainInput(e.target.value)} />
        </FilterField>
        <FilterField label="View" hint="Maps are stored per layout">
          <Form.Select size="sm" value={viewInput} onChange={(e) => setViewInput(e.target.value as "mobile" | "desktop")}>
            <option value="mobile">Mobile</option>
            <option value="desktop">Desktop</option>
          </Form.Select>
        </FilterField>
      </FilterBar>

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {map && !loading && (
        <>
          <div className="small text-muted mb-2">
            {map.exists ? "Stored map" : "No stored map yet; saving creates one"} · {map.checkoutTechnology ?? "unknown platform"} · last verified{" "}
            {map.lastVerifiedAt ? new Date(map.lastVerifiedAt).toLocaleString() : "never"} · API:{" "}
            {map.served ? (map.served.found ? `serving the ${map.served.viewType} map` : "no map served") : "lookup failed"}
          </div>
          <div className="small text-muted mb-2">
            Staging template ({map.templateView}):{" "}
            {map.staging
              ? `${map.staging.invalid ? "marked invalid · " : ""}${
                  map.staging.syncedToSqlAt ? `last put live ${new Date(map.staging.syncedToSqlAt).toLocaleString()}` : "never put live"
                }${map.staging.unsynced ? " · has changes not put live" : " · up to date"}${
                  map.staging.skuFromView ? ` · SKU fields from the ${map.staging.skuFromView} template` : ""
                }`
              : "none yet"}
          </div>
          <Alert variant="info" className="small py-2 d-flex flex-wrap gap-2 align-items-center">
            <span className="flex-grow-1" style={{ minWidth: 280 }}>
              The API serves fields marked <Badge bg="dark">SQL template</Badge> from the live coupon/SKU templates. Saving one here updates the stored
              map and stages it on the {map.templateView} Auto-coupon template. Nothing goes live automatically: a person puts the template live from
              Auto-coupon templates (search for {map.domain}).
            </span>
            <ToolbarButton
              label="Auto-coupon templates"
              icon="bi-box-arrow-up-right"
              to="/auto-coupons/templates"
              hint="Open the templates page to review the staged changes against live SQL and put them live"
            />
          </Alert>
          {map.stageResult && (map.stageResult.staged.length > 0 || map.stageResult.skipped) && (
            <Alert variant={map.stageResult.skipped ? "warning" : "success"} className="small py-2">
              {map.stageResult.staged.length > 0 && <div>Staged for SQL, not live yet: {map.stageResult.staged.map((p) => map.fields.find((f) => f.path === p)?.label ?? p).join(", ")}.</div>}
              {map.stageResult.skipped && <div>Not staged: {map.stageResult.skipped}. The stored map was still saved.</div>}
            </Alert>
          )}

          {SECTIONS.map((section) => {
            const fields = map.fields.filter((f) => f.section === section.key);
            return (
              <Card key={section.key} className="mb-3">
                <Card.Header className="py-2">
                  <strong>{section.title}</strong>
                </Card.Header>
                <Table size="sm" responsive className="small mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "18%" }}>Field</th>
                      <th style={{ width: "34%" }}>Stored value</th>
                      <th>Open Data API serves</th>
                      <th title="Map values are published by the hourly merge only after a journey test proves them">Journey test</th>
                      <th title="The value on the Auto-coupon staging template, and whether the API serves it yet">SQL staging</th>
                      <th>Source · health</th>
                      <th className="text-center" title="Keep this value through automated captures and self-heal">
                        Pin
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => {
                      const d = draftOf(f);
                      return (
                        <tr key={f.path}>
                          <td>
                            <div>{f.label}</div>
                            <div className="text-muted font-monospace" style={{ fontSize: 11 }}>
                              {f.apiField}
                            </div>
                            {f.servedFrom === "sql" && (
                              <Badge bg="dark" title="The API serves this field from the live SQL template">
                                SQL template
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className="font-monospace"
                              value={d.value}
                              disabled={!canEdit}
                              placeholder={f.kind === "pattern" ? "URL regex" : "CSS selector"}
                              onChange={(e) => setDraft(f, { value: e.target.value })}
                            />
                          </td>
                          <td>{served(f)}</td>
                          <td>{proofCell(f)}</td>
                          <td>
                            {staged(f, !!map.staging)}
                            {canEdit && f.stagingField && f.value && f.value !== f.stagedValue && !drafts[f.path] && (
                              <div className="mt-1">
                                <ToolbarButton
                                  label="Stage stored value"
                                  icon="bi-upload"
                                  hint="Add this field's stored value to the next save, so it is written to the staging template (not put live)"
                                  onClick={() => setDraft(f, { stage: true })}
                                />
                              </div>
                            )}
                            {drafts[f.path]?.stage && <div className="text-muted mt-1">will stage on save</div>}
                          </td>
                          <td className="text-nowrap">
                            {f.source ?? <span className="text-muted">—</span>}
                            {f.successCount !== null && (
                              <span className="text-muted">
                                {" "}
                                · {f.successCount}✓ {f.failureCount}✗
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={d.pin}
                              disabled={!canEdit}
                              title={f.pin ? `Pinned by ${f.pin.byName ?? f.pin.by} on ${new Date(f.pin.at).toLocaleDateString()}: ${f.pin.note ?? ""}` : "Pin this value"}
                              onChange={(e) => setDraft(f, { pin: e.target.checked })}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card>
            );
          })}

          {canEdit && (
            <Card className="mb-3">
              <Card.Body className="d-flex flex-wrap gap-2 align-items-center">
                <Form.Control
                  size="sm"
                  style={{ maxWidth: 520 }}
                  value={comment}
                  placeholder="Why this change? (required, shown in the history)"
                  onChange={(e) => setComment(e.target.value)}
                />
                <ToolbarButton
                  label={saving ? "Saving…" : `Save ${changes.length || ""} change${changes.length === 1 ? "" : "s"}`}
                  icon="bi-check2"
                  variant="primary"
                  hint="Validate the selectors and patterns, save them as manual values, stage SQL template fields for put-live, and record the change with your comment"
                  disabled={saving || !changes.length || comment.trim().length < 3}
                  onClick={() => void submit()}
                />
                {saveError && <span className="text-danger small">{apiErrorMessage(saveError)}</span>}
              </Card.Body>
            </Card>
          )}

          <Card className="mb-3">
            <Card.Header className="py-2">
              <strong>Change history</strong>
            </Card.Header>
            <Card.Body>
              <ChangeLogTable
                filter={{ entityType: "merchant_map", entityId: `${map.domain}|${map.templateView}` }}
                refreshKey={refreshKey}
                emptyTitle="No manual changes yet"
              />
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};
